/**
 * Hook for managing navigation state and API data fetching
 *
 * What it does:
 * - Fetches navigation steps from the API
 * - Converts raw API data into a usable format
 * - Stores the navigation data in Redux
 * - Returns loading states and navigation info
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../services/serviceTypes';
import type { NavigationData } from '../data-layer/reduxTypes';
import { useGetOnboardingStepsCategoryQuery } from '../data-store/copilotApi';
import { setNavigationSteps } from '../data-store/copilotSlice';
import parseOnboardingSteps from '../utils/leftNavHelpers';

interface UseNavigationStateReturn {
  navigation: NavigationData;
  isLoading: boolean;
  isError: boolean;
}

const useNavigationState = (): UseNavigationStateReturn => {
  const dispatch = useDispatch();
  // Get current navigation data from Redux store
  const navigation = useSelector(
    (state: RootState) => state.copilot.navigation
  );

  // Make API call to fetch navigation steps
  const { data, isLoading, isError } = useGetOnboardingStepsCategoryQuery();

  useEffect(() => {
    // When API call succeeds and we have data
    if (data && !isLoading && !isError) {
      // Convert raw API data into navigation-friendly format
      const parsedData = parseOnboardingSteps(data);

      // Save the processed data to Redux store
      dispatch(setNavigationSteps(parsedData));
    }
  }, [data, isLoading, isError, dispatch]);

  // Return navigation data and loading states
  return { navigation, isLoading, isError };
};

export default useNavigationState;
